import { Directive, ElementRef, HostBinding, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

type LoadingIndicatorConfig =
  | boolean
  | {
      disabled: boolean;
      loading: boolean;
    };

@Directive({
  selector: '[thLoadingIndicator]'
})
export class LoadingIndicatorDirective implements OnInit, OnDestroy {
  @HostBinding('disabled') disabled: boolean;

  @Input('thLoadingIndicator')
  set config(config: LoadingIndicatorConfig) {
    this._config = config;

    this.toggleLoadingIndicator();
  }

  get config(): LoadingIndicatorConfig {
    return this._config;
  }

  private spinnerContainer: HTMLElement;
  private _config: boolean | LoadingIndicatorConfig = false;
  private buttonTextColor: string;
  private initialized = false;

  get showLoadingIndicator(): boolean {
    return this.isBooleanConfig(this.config) ? Boolean(this.config) : this.config.loading;
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.initializeSpinner();
    this.toggleLoadingIndicator();
  }

  ngOnDestroy() {
    this.renderer.removeChild(this.elementRef.nativeElement, this.spinnerContainer);
  }

  private initializeSpinner() {
    if (!this.initialized) {
      this.buttonTextColor = getComputedStyle(<HTMLElement>this.elementRef.nativeElement).color;

      this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');

      this.spinnerContainer = this.renderer.createElement('i');
      this.renderer.setStyle(this.spinnerContainer, 'display', 'none');
      this.renderer.setAttribute(this.spinnerContainer, 'class', 'fas fa-spinner fa-spin button-spinner');

      this.renderer.appendChild(this.elementRef.nativeElement, this.spinnerContainer);

      this.initialized = true;
    }
  }

  private toggleLoadingIndicator() {
    if (!this.initialized) {
      return;
    }

    if (this.showLoadingIndicator) {
      // Show spinner
      this.renderer.removeStyle(this.spinnerContainer, 'display');

      // Apply button's text color to the spinner
      this.renderer.setStyle(this.spinnerContainer, 'color', this.buttonTextColor);

      // Make the button's color transparent (hides the text)
      this.renderer.setStyle(this.elementRef.nativeElement, 'color', 'transparent');
    } else {
      // Hide spinner
      this.renderer.setStyle(this.spinnerContainer, 'display', 'none');

      // Restore the button's original text color
      this.renderer.removeStyle(this.elementRef.nativeElement, 'color');
    }

    // Disable/enable the button
    if (this.isBooleanConfig(this.config)) {
      this.disabled = this.showLoadingIndicator;
    } else {
      this.disabled = this.config.disabled || this.showLoadingIndicator;
    }
  }

  private isBooleanConfig(x: LoadingIndicatorConfig): x is boolean {
    return x === true || x === false || x == null;
  }
}
